import qs from 'qs';
import {
  fetchApi,
  getFetchApiJSONResponse,
  getFetchApiTextResponse,
  catchFetchApiError,
} from 'shared/functions';
import {
  getSubscriptionRatingsKey,
  getSubscriptionRatingsType,
} from 'shared/constants/subscriptionRatings';
import {
  RatingStatuses,
  RatingType,
  ContractType,
  SubscriberConfirmation,
  SubscriptionRatingType,
} from '../types';

export const getUsers = async (
  type: SubscriptionRatingType,
  page: number,
  limit: number = 25,
  sort?: { sort: string; dir: string },
  searched?: string,
) => {
  const params = {
    page,
    limit,
    sort: sort?.sort,
    dir: sort?.dir,
    ...(searched && searched.length >= 3 && { name: searched }),
  };

  return fetchApi(
    `/storefront/ratings/${getSubscriptionRatingsKey(type)}/users?${qs.stringify(params)}`,
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const filterUsers = async (email: string) => {
  return fetchApi(`/storefront/users?${qs.stringify({ email })}`)
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const getRatings = async (
  type: SubscriptionRatingType,
  page: number,
  limit: number = 25,
  sort?: { sort: string; dir: string },
  searched?: string,
) => {
  const params = {
    page,
    limit,
    sort: sort?.sort,
    dir: sort?.dir,
    ...(searched && searched.length >= 3 && { name: searched }),
  };

  return fetchApi(`/storefront/ratings/${getSubscriptionRatingsKey(type)}?${qs.stringify(params)}`)
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const getRating = async (type: SubscriptionRatingType, ratingId: string) => {
  return fetchApi(`/storefront/ratings/${getSubscriptionRatingsKey(type)}/${ratingId}`)
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const getRatingUsers = async (
  type: SubscriptionRatingType,
  ratingId: string,
  page: number,
  limit: number = 25,
  sort?: { sort: string; dir: string },
  searched?: string,
) => {
  const params = {
    page,
    limit,
    sort: sort?.sort,
    dir: sort?.dir,
    ...(searched && searched.length >= 3 && { name: searched }),
  };

  return fetchApi(
    `/storefront/ratings/${getSubscriptionRatingsKey(type)}/${ratingId}/users?${qs.stringify(
      params,
    )}`,
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const getUserRatingStatusLog = async (
  type: SubscriptionRatingType,
  userId: string,
  ratingId: string,
  page: number,
  limit: number = 25,
) => {
  const params = {
    page: page + 1,
    limit,
  };

  return fetchApi(
    `/storefront/ratings/${getSubscriptionRatingsKey(
      type,
    )}/audit/${ratingId}/${userId}?${qs.stringify(params)}`,
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const setRatingContractType = async (
  type: SubscriptionRatingType,
  entityIds: [string],
  contractType: ContractType,
) => {
  return fetchApi(`/storefront/ratings/${getSubscriptionRatingsKey(type)}/entities/contract-type`, {
    method: 'POST',
    body: JSON.stringify({
      entityIds,
      contractType,
    }),
  }).catch(catchFetchApiError);
};

export const requestRating = async (rsrId: string, type = 'RESTRICTED_SUBSCRIPTION') =>
  fetchApi(
    `/storefront/ratings/${getSubscriptionRatingsKey(
      getSubscriptionRatingsType(type),
    )}/${rsrId}/request`,
    {
      method: 'POST',
    },
  ).catch(catchFetchApiError);

export const setSubscriberConfirmation = async (
  organizationIds: [string],
  confirmation: SubscriberConfirmation,
) => {
  return fetchApi(`/storefront/subscriber_confirmation?confirmation=${confirmation}`, {
    method: 'PUT',
    body: JSON.stringify(organizationIds),
  }).catch(catchFetchApiError);
};

export const addUser = async (
  type: SubscriptionRatingType,
  email: string,
  ratingIds: Array<{ entityId: string; ratingId: string; entityType: string }>,
) => {
  const body = {
    email,
  } as { email: string; ratingEntityList?: Array<any> };

  if (ratingIds.length) {
    body.ratingEntityList = ratingIds.map((rating) => {
      return { ratingId: rating.ratingId, ratingEntityType: rating.entityType };
    });
  }

  return fetchApi(`/storefront/ratings/${getSubscriptionRatingsKey(type)}/users/create`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
    .then(getFetchApiTextResponse)
    .catch(catchFetchApiError);
};

export const updateUserRatings = async (
  type: SubscriptionRatingType,
  userId: string,
  ratingIds: Array<{ entityId: string; ratingId: string; entityType: string }>,
) => {
  return fetchApi(`/storefront/ratings/${getSubscriptionRatingsKey(type)}/assignments/set`, {
    method: 'POST',
    body: JSON.stringify([
      {
        userId,
        ratingEntityList: ratingIds.map((rating) => {
          return {
            ratingId: rating.ratingId,
            ratingEntityType: rating.entityType,
            status: RatingStatuses.APPROVED,
          };
        }),
      },
    ]),
  }).catch(catchFetchApiError);
};

export const addRatingUsers = async (
  type: SubscriptionRatingType,
  ratingId: string,
  ratingType: RatingType,
  userIds: Array<string>,
) => {
  const body: any[] = [];
  userIds.forEach((userId) => {
    body.push({
      userId,
      ratingEntityList: [{ ratingId, ratingEntityType: ratingType }],
    });
  });
  return fetchApi(`/storefront/ratings/${getSubscriptionRatingsKey(type)}/assignments`, {
    method: 'PUT',
    body: JSON.stringify(body),
  }).catch(catchFetchApiError);
};

export const updateRatingUsers = async (
  type: SubscriptionRatingType,
  ratingId: string,
  userIds: Array<string>,
) => {
  return fetchApi(
    `/storefront/ratings/${getSubscriptionRatingsKey(type)}/assignments/updatestatus`,
    {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    },
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const deactivateUsers = async (type: SubscriptionRatingType, userIds: Array<string>) => {
  return fetchApi(`/storefront/ratings/${getSubscriptionRatingsKey(type)}/users/updatestatus`, {
    method: 'POST',
    body: JSON.stringify({ status: RatingStatuses.REVOKED, userIds }),
  }).catch(catchFetchApiError);
};

export const deactivateRatings = async (type: SubscriptionRatingType, entityIds: Array<string>) => {
  return fetchApi(`/storefront/ratings/${getSubscriptionRatingsKey(type)}/entities/updatestatus`, {
    method: 'POST',
    body: JSON.stringify({ status: RatingStatuses.REVOKED, entityIds }),
  }).catch(catchFetchApiError);
};

export const activateRatingUsers = async (
  type: SubscriptionRatingType,
  ratingId: string,
  ratingType: RatingType,
  userIds: Array<string>,
) => {
  const body: any[] = [];
  userIds.forEach((userId) => {
    body.push({
      userId,
      ratingEntityList: [
        { ratingId, ratingEntityType: ratingType, status: RatingStatuses.APPROVED },
      ],
    });
  });
  return fetchApi(
    `/storefront/ratings/${getSubscriptionRatingsKey(type)}/assignments/updatestatus`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const deactivateRatingUsers = async (
  type: SubscriptionRatingType,
  ratingId: string,
  ratingType: RatingType,
  userIds: Array<string>,
) => {
  const body: any[] = [];
  userIds.forEach((userId) => {
    body.push({
      userId,
      ratingEntityList: [
        { ratingId, ratingEntityType: ratingType, status: RatingStatuses.REVOKED },
      ],
    });
  });
  return fetchApi(
    `/storefront/ratings/${getSubscriptionRatingsKey(type)}/assignments/updatestatus`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};
